import React from 'react';
import { Checkbox, FormControlLabel, Stack } from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';
import { Button } from '../Button';
import { SplitButton, SplitButtonOption } from '../SplitButton';

export interface DangerZoneActionProps {
  actionIcon?: React.ReactNode;
  actionLabel: React.ReactNode;
  active: boolean;
  disabled?: boolean;
  label: React.ReactNode;
  onAction: () => void;
  onActiveChange: (active: boolean) => void;
  onSelectedActionChange?: (value: string) => void;
  selectedAction?: string;
  selectionOptions?: SplitButtonOption[];
}

export interface CondensedDangerZoneActionProps {
  actionIcon?: React.ReactNode;
  actionLabel: React.ReactNode;
  background?: string;
  disabled?: boolean;
  label: React.ReactNode;
  onAction: () => void;
}

const containerStyles = (theme: Theme) => ({
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 3,
  mt: 1,
  p: 2.5,
  border: '1px solid',
  borderColor: alpha(theme.palette.error.main, 0.24),
  borderRadius: 1.5,
  bgcolor: alpha(theme.palette.error.main, 0.055),
});
const labelContainerStyles = { m: 0 };
const labelProps = { typography: { sx: { fontSize: '0.875rem' } } };
const deleteButtonStyles = (theme: Theme) => ({
  minHeight: 40,
  px: 2.25,
  borderRadius: 999,
  fontWeight: 650,
  '&.Mui-disabled': {
    color: alpha(theme.palette.error.main, 0.55),
    borderColor: alpha(theme.palette.error.main, 0.35),
  },
});
const selectedDeleteButtonStyles = (theme: Theme) => ({
  ...deleteButtonStyles(theme),
  color: theme.palette.common.white,
  '&.Mui-disabled': {
    color: alpha(theme.palette.common.white, 0.48),
    bgcolor: alpha(theme.palette.error.main, 0.3),
  },
});
const condensedContainerStyles = {
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  mt: 0.5,
  p: 1,
};
const condensedLabelStyles = {
  color: 'error.dark',
  fontSize: '0.75rem',
  fontWeight: 600,
};
const condensedButtonStyles = {
  borderRadius: 2,
};

const DangerZoneAction = React.memo(
  ({
    actionIcon,
    actionLabel,
    active,
    disabled = false,
    label,
    onAction,
    onActiveChange,
    onSelectedActionChange,
    selectedAction,
    selectionOptions,
  }: DangerZoneActionProps) => {
    const hasSelection = Boolean(
      selectionOptions?.length && onSelectedActionChange && selectedAction
    );
    const handleActiveChange = (
      _event: React.ChangeEvent<HTMLInputElement>,
      checked: boolean
    ) => {
      onActiveChange(checked);
    };
    const actionButton = (
      <Button
        disabled={!active || disabled}
        onClick={onAction}
        startIcon={actionIcon}
        sx={deleteButtonStyles}
        tone="danger"
        variant="outlined"
      >
        {actionLabel}
      </Button>
    );

    return (
      <Stack direction="row" sx={containerStyles}>
        <FormControlLabel
          sx={labelContainerStyles}
          componentsProps={labelProps}
          control={
            <Checkbox
              checked={active}
              color="error"
              onChange={handleActiveChange}
              size="small"
            />
          }
          label={label}
        />
        {hasSelection ? (
          <SplitButton
            disabled={!active || disabled}
            label={actionLabel}
            menuButtonAriaLabel="Choose delete action"
            onClick={onAction}
            onSelectionChange={onSelectedActionChange!}
            options={selectionOptions!}
            startIcon={actionIcon}
            sx={selectedDeleteButtonStyles}
            tone="danger"
            value={selectedAction!}
            variant="contained"
          />
        ) : (
          actionButton
        )}
      </Stack>
    );
  }
);

DangerZoneAction.displayName = 'DangerZoneAction';

const CondensedDangerZoneAction = React.memo(
  ({
    actionIcon,
    actionLabel,
    background = 'transparent',
    disabled = false,
    label,
    onAction,
  }: CondensedDangerZoneActionProps) => (
    <Stack
      direction="row"
      sx={[condensedContainerStyles, { bgcolor: background }]}
    >
      <Stack component="span" sx={condensedLabelStyles}>
        {label}
      </Stack>
      <Button
        density="compact"
        disabled={disabled}
        onClick={onAction}
        startIcon={actionIcon}
        sx={condensedButtonStyles}
        tone="danger"
        variant="outlined"
      >
        {actionLabel}
      </Button>
    </Stack>
  )
);

CondensedDangerZoneAction.displayName = 'CondensedDangerZoneAction';

export { CondensedDangerZoneAction, DangerZoneAction };
